import React, { useCallback, useEffect, useState } from "react";
import { Download, Plus, Trash2, Pencil, Upload, RefreshCw, ChevronDown, Mail, FileUp } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";
const AUTH_KEY = "eviq_admin_token";
const authHeaders = () => {
  const t = localStorage.getItem(AUTH_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const jfetch = (path, opts = {}) =>
  fetch(`${API}/api/admin${path}`, {
    credentials: "include",
    ...opts,
    headers: { "Content-Type": "application/json", ...authHeaders(), ...(opts.headers || {}) },
  });

const usd = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);
const EMPTY_FORM = { date: new Date().toISOString().slice(0, 10), amount: "", category: "Credit Top-up", vendor: "Emergent Labs", note: "" };

const SOURCE_BADGE = {
  manual: { label: "manual", color: "#71717a" },
  import: { label: "imported", color: "#00E5FF" },
  recurring: { label: "auto", color: "#39FF14" },
  gmail: { label: "gmail", color: "#FFB020" },
  upload: { label: "receipt", color: "#B98CFF" },
};

const inputCls = "bg-black border border-white/10 focus:border-[#39FF14]/50 focus:outline-none rounded-lg px-2.5 py-1.5 text-xs text-white w-full [color-scheme:dark]";

const RECEIPT_STATUS = {
  added: "#39FF14",
  duplicate: "#FFB020",
  unparsed: "#FF5C5C",
  unsupported: "#FF5C5C",
  error: "#FF5C5C",
};

const ReceiptUploadBlock = ({ onUploaded }) => {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  const upload = async () => {
    if (!files.length || busy) return;
    setBusy(true);
    setResults(null);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    try {
      const r = await fetch(`${API}/api/admin/expenses/upload`, {
        method: "POST", credentials: "include", headers: authHeaders(), body: fd,
      });
      if (r.ok) {
        const d = await r.json();
        setResults(d.results);
        setFiles([]);
        if (d.added > 0) await onUploaded();
      } else {
        setResults([{ file: "Upload", status: "error", detail: (await r.json()).detail || "failed" }]);
      }
    } catch (e) {
      setResults([{ file: "Upload", status: "error", detail: "network error" }]);
    }
    setBusy(false);
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-3 mb-3" data-testid="receipt-upload-block">
      <div className="text-xs text-white inline-flex items-center gap-2 mb-2">
        <FileUp className="w-3.5 h-3.5 text-zinc-500" strokeWidth={2} />
        Upload receipts — select multiple PDFs at once
      </div>
      <label
        className="flex flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed border-white/15 hover:border-[#39FF14]/40 bg-black/30 py-5 cursor-pointer transition-colors"
        data-testid="receipt-dropzone"
      >
        <input
          type="file" multiple accept="application/pdf,.pdf" className="hidden"
          onChange={(e) => setFiles(Array.from(e.target.files || []))}
          data-testid="receipt-file-input"
        />
        <Upload className="w-4 h-4 text-zinc-500" strokeWidth={2} />
        <span className="text-[11px] text-zinc-400">
          {files.length ? `${files.length} file(s) selected` : "Tap to choose PDF receipts (multi-select)"}
        </span>
      </label>
      {files.length > 0 && (
        <div className="mt-2 space-y-1">
          {files.map((f, i) => (
            <div key={i} className="text-[10px] font-mono text-zinc-500 truncate">{f.name}</div>
          ))}
          <button onClick={upload} disabled={busy} data-testid="receipt-upload-btn"
                  className="mt-1 text-xs font-mono uppercase tracking-wider px-4 py-2 rounded-lg border border-[#39FF14]/40 bg-[#39FF14]/10 hover:bg-[#39FF14]/20 disabled:opacity-40 text-white transition-colors">
            {busy ? "Parsing receipts…" : `Upload & parse ${files.length} receipt(s)`}
          </button>
        </div>
      )}
      {results && (
        <div className="mt-2 space-y-1" data-testid="receipt-upload-results">
          {results.map((r, i) => (
            <div key={i} className="flex items-center gap-2 text-[11px]">
              <span className="font-mono text-[9px] uppercase px-1.5 py-0.5 rounded shrink-0"
                    style={{ color: RECEIPT_STATUS[r.status] || "#71717a", background: `${RECEIPT_STATUS[r.status] || "#71717a"}18` }}>
                {r.status}
              </span>
              <span className="text-zinc-300 truncate">{r.file}</span>
              {r.amount ? <span className="font-mono text-white shrink-0">{usd(r.amount)} · {r.date}</span>
                        : <span className="text-zinc-500 truncate">{r.detail}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const GmailSyncBlock = ({ onSynced, setMsg }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadStatus = useCallback(async () => {
    const r = await jfetch("/gmail/status");
    if (r.ok) setStatus(await r.json());
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const syncNow = async () => {
    setBusy(true);
    const r = await jfetch("/gmail/sync", { method: "POST" });
    if (r.ok) {
      const res = await r.json();
      setMsg(`Gmail sync: ${res.added} receipt(s) added · ${res.skipped_duplicates} duplicates skipped · ${res.scanned} emails scanned`);
      await Promise.all([loadStatus(), onSynced()]);
    } else {
      setMsg(`Gmail sync failed: ${(await r.json()).detail || "error"}`);
    }
    setBusy(false);
  };

  const disconnect = async () => {
    await jfetch("/gmail/disconnect", { method: "POST" });
    await loadStatus();
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-3 mb-3" data-testid="gmail-sync-block">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs text-white inline-flex items-center gap-2">
          <Mail className="w-3.5 h-3.5 text-zinc-500" strokeWidth={2} />
          Gmail receipt auto-sync
          {status?.connected && (
            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ color: "#39FF14", background: "#39FF1418" }}>
              connected{status.email ? ` · ${status.email}` : ""}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!status ? null : !status.configured ? (
            <span className="text-[10px] font-mono text-[#FFB020]" data-testid="gmail-not-configured">
              Awaiting Google OAuth credentials in backend .env
            </span>
          ) : !status.connected ? (
            <button
              onClick={() => {
                window.location.href = `${API}/api/admin/gmail/connect`;
              }}
              data-testid="gmail-connect-btn"
              className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded-lg border border-[#00E5FF]/40 bg-[#00E5FF]/10 hover:bg-[#00E5FF]/20 text-white transition-colors"
            >
              Connect Gmail
            </button>
          ) : (
            <>
              <button onClick={syncNow} disabled={busy} data-testid="gmail-sync-btn"
                      className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded-lg border border-[#39FF14]/40 bg-[#39FF14]/10 hover:bg-[#39FF14]/20 disabled:opacity-40 text-white transition-colors">
                {busy ? "Scanning inbox…" : "Sync receipts now"}
              </button>
              <button onClick={disconnect} data-testid="gmail-disconnect-btn"
                      className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 underline underline-offset-2 hover:text-white transition-colors">
                Disconnect
              </button>
            </>
          )}
        </div>
      </div>
      {status?.connected && (
        <p className="mt-1.5 text-[11px] text-zinc-500 leading-relaxed">
          Scans your inbox daily for Emergent receipts/invoices and auto-creates expense entries
          (duplicates skipped).{status.last_sync ? ` Last sync: ${new Date(status.last_sync).toLocaleString()}.` : ""}
        </p>
      )}
    </div>
  );
};

export const OperatingExpensesCard = () => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [rulesOpen, setRulesOpen] = useState(false);
  const [rules, setRules] = useState([]);
  const [rulesDirty, setRulesDirty] = useState(false);

  const load = useCallback(async () => {
    const r = await jfetch("/expenses");
    if (r.ok) {
      const d = await r.json();
      setData(d);
      setRules(d.rules || []);
      setRulesDirty(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.date || !form.amount) return;
    setBusy(true);
    setMsg("");
    const body = JSON.stringify({ ...form, amount: Number(form.amount) });
    const r = editingId
      ? await jfetch(`/expenses/${editingId}`, { method: "PUT", body })
      : await jfetch("/expenses", { method: "POST", body });
    if (r.ok) {
      setForm(EMPTY_FORM);
      setEditingId(null);
      await load();
    } else {
      setMsg((await r.json()).detail || "Save failed");
    }
    setBusy(false);
  };

  const remove = async (id) => {
    await jfetch(`/expenses/${id}`, { method: "DELETE" });
    await load();
  };

  const startEdit = (e) => {
    setEditingId(e.id);
    setForm({ date: e.date, amount: String(e.amount), category: e.category, vendor: e.vendor, note: e.note || "" });
  };

  const runImport = async () => {
    setBusy(true);
    setMsg("");
    const r = await jfetch("/expenses/import", { method: "POST", body: JSON.stringify({ text: importText }) });
    if (r.ok) {
      const res = await r.json();
      setMsg(`Imported ${res.added} · skipped ${res.skipped_duplicates} duplicates${res.unparsed.length ? ` · ${res.unparsed.length} lines not recognized` : ""}`);
      setImportText("");
      await load();
    }
    setBusy(false);
  };

  const saveRules = async () => {
    setBusy(true);
    const r = await jfetch("/expenses/rules", { method: "POST", body: JSON.stringify({ rules }) });
    if (r.ok) await load();
    setBusy(false);
  };

  const runRulesNow = async () => {
    setBusy(true);
    const r = await jfetch("/expenses/rules/run", { method: "POST" });
    if (r.ok) {
      const res = await r.json();
      setMsg(`Auto-log run: ${res.logged} charge(s) logged for ${res.month}`);
      await load();
    }
    setBusy(false);
  };

  const exportCsv = async () => {
    const year = new Date().getFullYear();
    const r = await fetch(`${API}/api/admin/expenses/quickbooks.csv?year=${year}`, {
      credentials: "include", headers: authHeaders(),
    });
    if (!r.ok) return;
    const blob = await r.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `EVIQ_Operating_Expenses_${year}_QuickBooks.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const s = data?.summary;
  const cats = data?.categories || [];

  return (
    <div data-testid="operating-expenses-section" className="rounded-3xl border border-white/10 bg-white/[0.02] p-5 sm:p-6">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">
          Operating expenses · Emergent payments
        </div>
        <button
          onClick={exportCsv}
          data-testid="expenses-export-csv"
          className="brand-gradient text-black font-semibold px-3 py-1.5 rounded-lg text-[10px] sm:text-xs font-mono uppercase tracking-wider inline-flex items-center gap-1.5 hover:opacity-90 transition-opacity"
        >
          <Download className="w-3.5 h-3.5" strokeWidth={2.5} /> QuickBooks CSV
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-3">
          <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">This month</div>
          <div className="text-lg text-white font-semibold" data-testid="expenses-month-total">{usd(s?.this_month_total)}</div>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-3">
          <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">YTD {s?.year}</div>
          <div className="text-lg text-white font-semibold" data-testid="expenses-ytd-total">{usd(s?.ytd_total)}</div>
        </div>
        <div className="col-span-2 rounded-2xl border border-white/10 bg-white/[0.02] p-3">
          <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">YTD by category</div>
          <div className="flex flex-wrap gap-x-3 gap-y-0.5" data-testid="expenses-by-category">
            {Object.entries(s?.by_category || {}).map(([c, v]) => (
              <span key={c} className="text-[11px] font-mono text-zinc-300">{c}: <span className="text-white">{usd(v)}</span></span>
            ))}
            {Object.keys(s?.by_category || {}).length === 0 && (
              <span className="text-[11px] text-zinc-600">No expenses recorded yet</span>
            )}
          </div>
        </div>
      </div>

      {/* Add / edit form */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-3 mb-3" data-testid="expense-form">
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
          <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                 className={inputCls} data-testid="expense-date-input" />
          <input type="number" min="0" step="0.01" placeholder="Amount $" value={form.amount}
                 onChange={(e) => setForm({ ...form, amount: e.target.value })}
                 className={inputCls} data-testid="expense-amount-input" />
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className={inputCls} data-testid="expense-category-select">
            {cats.map((c) => <option key={c} value={c} className="bg-black">{c}</option>)}
          </select>
          <input placeholder="Vendor" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                 className={inputCls} data-testid="expense-vendor-input" />
          <input placeholder="Note / invoice # (for accountant)" value={form.note}
                 onChange={(e) => setForm({ ...form, note: e.target.value })}
                 className={`${inputCls} sm:col-span-1`} data-testid="expense-note-input" />
          <button onClick={save} disabled={busy || !form.amount}
                  data-testid="expense-save-btn"
                  className="text-xs font-mono uppercase tracking-wider rounded-lg border border-[#39FF14]/40 bg-[#39FF14]/10 hover:bg-[#39FF14]/20 disabled:opacity-40 text-white inline-flex items-center justify-center gap-1.5 transition-colors">
            <Plus className="w-3.5 h-3.5" strokeWidth={2.5} /> {editingId ? "Update" : "Add"}
          </button>
        </div>
        {editingId && (
          <button onClick={() => { setEditingId(null); setForm(EMPTY_FORM); }}
                  className="mt-2 text-[10px] font-mono uppercase tracking-wider text-zinc-500 underline underline-offset-2"
                  data-testid="expense-cancel-edit">
            Cancel edit
          </button>
        )}
      </div>

      {/* Receipt PDF upload */}
      <ReceiptUploadBlock onUploaded={load} />

      {/* Gmail auto-sync */}
      <GmailSyncBlock onSynced={load} setMsg={setMsg} />

      {/* Bulk import */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] mb-3">
        <button onClick={() => setImportOpen(!importOpen)} data-testid="expense-import-toggle"
                className="w-full flex items-center justify-between p-3 hover:bg-white/[0.03] transition-colors">
          <span className="text-xs text-white inline-flex items-center gap-2">
            <Upload className="w-3.5 h-3.5 text-zinc-500" strokeWidth={2} />
            Bulk import — paste your billing history from the Stripe portal
          </span>
          <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${importOpen ? "rotate-180" : ""}`} />
        </button>
        {importOpen && (
          <div className="border-t border-white/5 p-3 space-y-2">
            <p className="text-[11px] text-zinc-500 leading-relaxed">
              In Emergent: Profile → Credits → Manage your Subscription → copy the payment rows
              (date, description, amount) and paste below, one charge per line. Duplicates are skipped automatically.
            </p>
            <textarea value={importText} onChange={(e) => setImportText(e.target.value)} rows={5}
                      placeholder={"Aug 1, 2026  Emergent subscription  $50.00\n08/01/2026  Credit top-up 250 credits  $50.00"}
                      className={`${inputCls} font-mono text-[11px]`} data-testid="expense-import-textarea" />
            <button onClick={runImport} disabled={busy || !importText.trim()} data-testid="expense-import-btn"
                    className="text-xs font-mono uppercase tracking-wider px-4 py-2 rounded-lg border border-[#00E5FF]/40 bg-[#00E5FF]/10 hover:bg-[#00E5FF]/20 disabled:opacity-40 text-white transition-colors">
              {busy ? "Importing…" : "Parse & import"}
            </button>
          </div>
        )}
      </div>

      {/* Recurring rules */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] mb-3">
        <button onClick={() => setRulesOpen(!rulesOpen)} data-testid="expense-rules-toggle"
                className="w-full flex items-center justify-between p-3 hover:bg-white/[0.03] transition-colors">
          <span className="text-xs text-white inline-flex items-center gap-2">
            <RefreshCw className="w-3.5 h-3.5 text-zinc-500" strokeWidth={2} />
            Recurring charges — auto-logged monthly
          </span>
          <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${rulesOpen ? "rotate-180" : ""}`} />
        </button>
        {rulesOpen && (
          <div className="border-t border-white/5 p-3 space-y-2" data-testid="expense-rules-editor">
            {rules.map((r, i) => (
              <div key={r.id} className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-center">
                <input value={r.label} onChange={(e) => { const n = [...rules]; n[i] = { ...r, label: e.target.value }; setRules(n); setRulesDirty(true); }}
                       className={`${inputCls} sm:col-span-2`} data-testid={`rule-label-${i}`} />
                <input type="number" min="0" step="0.01" value={r.amount}
                       onChange={(e) => { const n = [...rules]; n[i] = { ...r, amount: Number(e.target.value) }; setRules(n); setRulesDirty(true); }}
                       className={inputCls} data-testid={`rule-amount-${i}`} />
                <select value={r.day_of_month} onChange={(e) => { const n = [...rules]; n[i] = { ...r, day_of_month: Number(e.target.value) }; setRules(n); setRulesDirty(true); }}
                        className={inputCls} data-testid={`rule-day-${i}`}>
                  {Array.from({ length: 28 }, (_, d) => d + 1).map((d) => <option key={d} value={d} className="bg-black">Day {d}</option>)}
                </select>
                <label className="flex items-center gap-2 text-[11px] text-zinc-400 cursor-pointer">
                  <input type="checkbox" checked={r.active}
                         onChange={(e) => { const n = [...rules]; n[i] = { ...r, active: e.target.checked }; setRules(n); setRulesDirty(true); }}
                         data-testid={`rule-active-${i}`} className="accent-[#39FF14]" />
                  Active
                </label>
              </div>
            ))}
            <div className="flex items-center gap-2 pt-1">
              <button onClick={saveRules} disabled={!rulesDirty || busy} data-testid="rules-save-btn"
                      className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded-lg border border-[#39FF14]/40 bg-[#39FF14]/10 hover:bg-[#39FF14]/20 disabled:opacity-40 text-white transition-colors">
                Save rules
              </button>
              <button onClick={runRulesNow} disabled={busy} data-testid="rules-run-btn"
                      className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded-lg border border-white/15 hover:bg-white/5 text-zinc-300 transition-colors">
                Run auto-log now
              </button>
            </div>
          </div>
        )}
      </div>

      {msg && <div className="text-[11px] font-mono text-[#00E5FF] mb-2" data-testid="expenses-msg">{msg}</div>}

      {/* Entries table */}
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs" data-testid="expenses-table">
          <thead>
            <tr className="text-zinc-500 font-mono uppercase tracking-wider text-[10px] border-b border-white/10">
              <th className="py-2 pr-3">Date</th>
              <th className="py-2 pr-3">Vendor</th>
              <th className="py-2 pr-3">Category</th>
              <th className="py-2 pr-3 text-right">Amount</th>
              <th className="py-2 pr-3">Note</th>
              <th className="py-2 pr-3">Src</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {(data?.entries || []).map((e) => {
              const b = SOURCE_BADGE[e.source] || SOURCE_BADGE.manual;
              return (
                <tr key={e.id} className="border-b border-white/5 text-zinc-300" data-testid={`expense-row-${e.id}`}>
                  <td className="py-2 pr-3 font-mono whitespace-nowrap">{e.date}</td>
                  <td className="py-2 pr-3">{e.vendor}</td>
                  <td className="py-2 pr-3">{e.category}</td>
                  <td className="py-2 pr-3 text-right font-mono text-white">{usd(e.amount)}</td>
                  <td className="py-2 pr-3 text-zinc-500 max-w-[220px] truncate" title={e.note}>{e.note}</td>
                  <td className="py-2 pr-3">
                    <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded"
                          style={{ color: b.color, background: `${b.color}18` }}>{b.label}</span>
                  </td>
                  <td className="py-2 whitespace-nowrap">
                    <button onClick={() => startEdit(e)} className="p-1 text-zinc-500 hover:text-white transition-colors"
                            aria-label="Edit" data-testid={`expense-edit-${e.id}`}>
                      <Pencil className="w-3.5 h-3.5" strokeWidth={2} />
                    </button>
                    <button onClick={() => remove(e.id)} className="p-1 text-zinc-500 hover:text-red-400 transition-colors"
                            aria-label="Delete" data-testid={`expense-delete-${e.id}`}>
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
                    </button>
                  </td>
                </tr>
              );
            })}
            {(data?.entries || []).length === 0 && (
              <tr><td colSpan={7} className="py-4 text-center text-zinc-600 text-[11px]">
                No expenses yet — add one above or bulk-import your Stripe billing history.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-[11px] text-zinc-500 leading-relaxed">
        CSV export maps every entry to the <span className="text-zinc-300">"{"Software & SaaS"}"</span> expense
        account with payee, category and memo — ready for QuickBooks expense import and tax-accountant review.
      </p>
    </div>
  );
};
